import { createSlice } from '@reduxjs/toolkit';
import { detectInitialLocale } from '../i18n';

// Grid layout is per-user, but redux-persist keeps it in localStorage, which is per-browser.
// Logout has to put these back or the next user inherits the previous one's filters, hidden
// columns and saved query -- and on Tasks those can reference another org's custom columns.
// A factory rather than a shared object: the reset assigns these into an immer draft, which
// deep-freezes what it is handed.
const createInitialGridState = () => ({
    // `items: []` is MUI's own default. The previous `[{}]` put an item with no columnField in
    // the model, which the grid prunes on every mount -- firing onFilterModelChange and writing
    // the pruned model straight back over the stored one.
    taskFilters: { items: [] },
    taskSort: [],
    taskPageSize: 25,
    hazardFilters: { items: [] },
    hazardSort: [],
    hazardPageSize: 25,
    taskQueryId: '',
    hazardColumns: {
        hzId: true,
        hzOrganizationName: true,
        hzProject: true,
        hzRiskSource: true,
        hzDepartmentName: true,
        hzProcess: true,
        hzJob: true,
        hzTask: true,
        hzEquipmentId: true,
        hzRiskScoreUncontrolled: true,
        hzRiskLevelUncontrolled: true,
        hzRiskScoreControlled: true,
        hzRisKLevelControlled: true
    },
    // `{}`, not undefined: DataGridPro reads an undefined visibility model as uncontrolled and
    // would flip to controlled on the first toggle. An empty model means the same thing --
    // every column visible -- without the switch.
    taskColumns: {},
});

export const correcttrackSlice = createSlice({
    name: 'correcttrack',
    initialState: {
        // Held in memory for the life of the tab only -- see the persist config, which keeps
        // this out of localStorage. The refresh token is not here at all any more: it lives in an
        // httpOnly cookie the page cannot read.
        userToken: '',
        ...createInitialGridState(),
        language: detectInitialLocale(),
    },
    reducers: {
        setLanguage: (state, action) => {
            state.language = action.payload;
        },
        updateTaskFilters: (state, action) => {
            state.taskFilters = action.payload;
        },
        updateHazardFilters: (state, action) => {
            state.hazardFilters = action.payload;
        },
        updateTaskQuery: (state, action) => {
            state.taskQueryId = action.payload;
        },
        updateHazardColumns: (state, action) => {
            state.hazardColumns = action.payload;
        },
        updateTaskColumns: (state, action) => {
            state.taskColumns = action.payload;
        },
        updateTaskSort: (state, action) => {
            state.taskSort = action.payload;
        },
        updateTaskPageSize: (state, action) => {
            state.taskPageSize = action.payload;
        },
        updateHazardSort: (state, action) => {
            state.hazardSort = action.payload;
        },
        updateHazardPageSize: (state, action) => {
            state.hazardPageSize = action.payload;
        },
        logout: (state) => {
            state.userToken = '';
            state.forceResetPassword = false;
            // Drop the outgoing user's grid layout; see createInitialGridState.
            Object.assign(state, createInitialGridState());
        },
        updateUserToken: (state, action) => {
            // Only the access token comes back in the response body now; the server sets the
            // refresh token as a cookie the browser stores and sends on its own.
            state.userToken = action.payload.token;
        },
    }
});

export const {
    updateUserToken, logout, updateTaskFilters, updateHazardFilters, updateTaskQuery,
    updateHazardColumns, updateTaskColumns, setLanguage,
    updateTaskSort, updateTaskPageSize, updateHazardSort, updateHazardPageSize,
} = correcttrackSlice.actions;

export default correcttrackSlice.reducer;
